import { Navigate } from 'react-router-dom';

import EmptyState from 'components/common/EmptyState';
import { useAuth } from 'hooks/useAuth';
import { resolveLandingPath } from 'util/landing';

/**
 * Landing page for an account whose role currently grants no module at all.
 *
 * Reachable because every module — the dashboard included — is a permission now,
 * so a role can legitimately be left holding nothing. Showing this beats
 * bouncing between guards or landing on a page that immediately 403s: it says
 * plainly that access is a setting someone else controls, and who to ask.
 *
 * It re-checks access on every permission update (AuthContext reloads them on
 * an `access.changed` push, on focus and every 60s). It used to be static, so
 * once an admin granted a module back the sidebar filled in while this page
 * kept saying "No modules" until the user navigated away or signed in again.
 */
export default function NoModules() {
  const { role, user, can, permissions, principal, hasResourceGrant } = useAuth();
  const permsLoaded = Boolean(permissions);
  const landing = permsLoaded
    ? resolveLandingPath({
      can,
      role,
      principal,
      hasCenter: Boolean(user?.center_id ?? user?.centerId),
      assignments: user?.assignments,
      roleNames: user?.roles,
      hasResourceGrant,
      permsLoaded,
    })
    : null;
  if (landing) return <Navigate to={landing} replace />;
  return (
    <EmptyState
      title="No modules assigned yet"
      description="Your role doesn't currently include access to any module. Ask your admin to grant the permissions you need — this page updates on its own as soon as they do."
    />
  );
}
